import { ParsedUrlQuery } from 'querystring';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'next/navigation';

import {
  convertDateToMomentObject,
  DateType,
  HOTEL_SEARCH_DATE_FORMAT,
} from '@utils/dateUtil';
import { FiltersInterface } from '@contexts/hotel/HotelListing';
import { HotelListFiltersViewModel, PackageListViewModel } from '@api/v3';
import { ListingMetaViewModel } from '@components/views/types';
import { useAuthState } from '@contexts/auth/AuthContext';
import { callService } from '@api/remote-call';
import clean from '@utils/cleanObject';
import { paramsToObject } from '@components/views/utils';

interface useListingFetchingProps {
  setIsFetchedItems: (val: boolean) => void;
  setAdultCount: React.Dispatch<React.SetStateAction<number>>;
  setChildCount: React.Dispatch<React.SetStateAction<number>>;
  setFilters: React.Dispatch<React.SetStateAction<Partial<FiltersInterface>>>;
  setDates: React.Dispatch<
    React.SetStateAction<{
      startDate: DateType | null;
      endDate: DateType | null;
    }>
  >;
  basketId?: string;
  initialData?: PackageListViewModel;
  enableFetch?: boolean;
  packageTabChanged?: boolean;
}

export const useListingFetching = ({
  setIsFetchedItems,
  setAdultCount,
  setChildCount,
  setDates,
  setFilters,
  initialData,
  packageTabChanged,
}: useListingFetchingProps) => {
  const searchParams = useSearchParams();
  const [isClientSide] = useState(false);
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const params = useParams();

  const { loginInfo } = useAuthState();
  const formattedRoom = query
    ? Array.isArray(query)
      ? query.map((item) => item.room.replaceAll('_', ','))
      : typeof query.room === 'string'
      ? query.room.replaceAll('_', ',')
      : ''
    : '';

  const fetchPackageItems = useCallback(
    (basketId?: string) =>
      callService(
        { service: 'PackagesService', method: 'listPackages', version: 'v3' },
        clean({
          slug: params?.slug as string,
          _in: query.in as string,
          out: query.out as string,
          sort: query.sort as string,
          region: query.region as string[],
          theme: query.theme as string[],
          popularity: query.popularity as string[],
          feature: query.feature as string[],
          boardType: query.boardType as string[],
          point: query.point as string[],
          refundable: query.refundable as string,
          keywords: query.keywords as string,
          status: query.status as string,
          priceMin: query.priceMin as string,
          priceMax: query.priceMax as string,
          room: formattedRoom as unknown as string,
          departure: query.dep as string,
          departureIsCity: query.depIsCity ? Boolean(query.depIsCity) : false,
          arrival: 'ECN',
          arrivalIsCity: false,
          oneWay: query.oneWay ? Boolean(query.oneWay) : false,
          direct: query.direct ? Boolean(query.direct) : false,
          page: query.page ? (query.page as string) : '1',
          size: query.size as string,
          star: query.star as string[],
          ...(basketId ? { basketId } : {}),
        })
      ),
    [query]
  );

  const fetchPackages = () => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');
    return fetchPackageItems(String(basketId));
  };

  const { page } = query;
  const {
    data,
    isError,
    isFetching,
    isInitialLoading: isLoading,
    isSuccess: isListingSuccess,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ['packageListing', JSON.stringify(query), JSON.stringify(loginInfo ?? '')],
    fetchPackages,
    {
      enabled: Boolean(params?.slug && isClientSide) && !packageTabChanged,
      getNextPageParam: () => {
        return page ? page : 1;
      },

      initialData: initialData
        ? () => {
            return {
              pages: [initialData],
              pageParams: [initialData?.hotels?.meta?.currentPage],
            };
          }
        : undefined,
    }
  );

  useEffect(() => {
    if (query && query.room) {
      const roomsAdult = query.room
        ?.toString()
        .split(',')
        .map((item) => (item.includes('-') ? '' : item));

      const roomsChild = query.room
        ?.toString()
        .split(',')
        .map((item) => (item.includes('-') ? item : ''))
        .filter((item) => item !== '');

      const adultNumber = roomsAdult.reduce((a, b) => Number(a) + Number(b), 0);

      setAdultCount(adultNumber);
      setChildCount(roomsChild.length);
    }

    if (query.keywords) {
      setFilters((prev) => ({
        ...prev,
        hotelName: String(query.keywords),
      }));
    }

    setDates({
      startDate: query.in
        ? convertDateToMomentObject(
            query.in as string,
            HOTEL_SEARCH_DATE_FORMAT
          )
        : null,
      endDate: query.out
        ? convertDateToMomentObject(
            query.out as string,
            HOTEL_SEARCH_DATE_FORMAT
          )
        : null,
    });
  }, [query]);

  useEffect(() => {
    if (!data) return;

    if (isError) return setIsFetchedItems(false);

    setIsFetchedItems(false);
    setFilters((prev) => ({
      ...prev,
      regions:
        (data.pages?.length &&
          data?.pages?.[0]?.hotels?.filters?.filter(
            (item: HotelListFiltersViewModel) => item.type === 'region'
          )[0]?.items) ||
        [],
    }));
  }, [data]);

  const meta = useMemo(() => {
    const meta = data?.pages?.[data?.pages?.length - 1]?.hotels?.meta;
    let metaData: ListingMetaViewModel;
    if (meta) {
      metaData = { ...meta, hasNextPage: !!hasNextPage };
      return metaData;
    }
  }, [data, hasNextPage]);

  return {
    hotels: data?.pages?.map((page) => page?.hotels?.data) || [],
    flights: data?.pages[0].flights || [],
    transfers: data?.pages[0].transfers || [],
    //banners: data?.pages?.[0]?.banner as ListingBannerViewModel,
    filters: data?.pages?.[0]?.hotels?.filters,
    isError,
    isLoading,
    isFetching,
    isListingSuccess,
    fetchNextPage,
    hasNextPage,
    refetch,
    meta,
  };
};
